import React from 'react';
import { Typography, Grid, Box, Chip, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { motion } from 'framer-motion';
import GradientText from '../shared/GradientText';
import AnimatedCard from '../shared/AnimatedCard';
import SectionWrapper from '../shared/SectionWrapper';
import { services } from '../../data/services';

const ServicesSection: React.FC = () => (
  <SectionWrapper id="services" dark>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <Box sx={{ mb: 10 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
        >
          CONSULTING
        </Typography>
        <GradientText variant="h2" sx={{ mb: 3, maxWidth: 500 }}>
          Consulting Services
        </GradientText>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 550 }}>
          Need hands-on engineering help? Our consulting team delivers end-to-end
          solutions across the data and software stack — from architecture to production.
        </Typography>
      </Box>
    </motion.div>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {services.map((service, i) => (
        <AnimatedCard key={service.title} delay={i * 0.08} sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    flexShrink: 0,
                  }}
                >
                  <service.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                  {service.title}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                {service.description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {service.highlights.map((h) => (
                  <Chip
                    key={h}
                    label={h}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      color: 'text.secondary',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <List disablePadding>
                {service.details.map((detail) => (
                  <ListItem key={detail} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 24, mt: 0.7 }}>
                      <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={detail}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.secondary',
                        sx: { lineHeight: 1.7 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </AnimatedCard>
      ))}
    </Box>
  </SectionWrapper>
);

export default ServicesSection;
